import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  SetManagerNotificationChannelRequestSchema,
  type ManagerNotificationChannel,
  type ManagerSettingsResponse,
} from '@besideu/shared/manager-settings';
import { api } from '@/lib/api.ts';
import { translateSaveError } from '@/lib/settings-errors.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select } from '@/components/ui/Field.tsx';

const CHANNEL_COPY: Record<ManagerNotificationChannel, { label: string; hint: string }> = {
  auto: {
    label: 'Auto',
    hint: "Uses your organisation's connected workspace (Slack or Google Workspace) if one is set up, otherwise email.",
  },
  email: {
    label: 'Always email me',
    hint: 'Skips workspace messaging entirely, even if your organisation has one connected.',
  },
};

export function ManagerSettings() {
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['manager', 'settings'],
    queryFn: () => api.get<ManagerSettingsResponse>('/api/manager/settings'),
  });

  const [channel, setChannel] = useState<ManagerNotificationChannel>('auto');

  // Seed from the query result exactly once, on first load — matches
  // HrSettings.tsx's pattern. `save`'s `onSuccess` below invalidates the
  // query, which refetches; re-seeding on that refetch would overwrite
  // a selection made after clicking Save but before the refetch resolves.
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (!settings.data || hasSeeded.current) return;
    hasSeeded.current = true;
    setChannel(settings.data.notificationChannel);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (input: { notificationChannel: ManagerNotificationChannel }) =>
      api.patch<{ ok: true }>('/api/manager/settings', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manager', 'settings'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = SetManagerNotificationChannelRequestSchema.safeParse({
      notificationChannel: channel,
    });
    if (!parsed.success) return;
    save.mutate(parsed.data);
  }

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-2 text-muted-foreground">How besideU reaches you about your team.</p>
      </header>

      {settings.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : settings.error ? (
        <p className="text-traffic-red">Could not load settings.</p>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="max-w-2xl space-y-6 rounded-lg border border-border p-6"
        >
          <div>
            <h2 className="text-lg font-semibold">Notifications</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              How touch point reminders and overdue nudges reach you.
            </p>
          </div>

          <Field label="Notification channel" hint={CHANNEL_COPY[channel].hint}>
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={channel}
                onChange={(e) => setChannel(e.target.value as ManagerNotificationChannel)}
              >
                {(Object.keys(CHANNEL_COPY) as ManagerNotificationChannel[]).map((value) => (
                  <option key={value} value={value}>
                    {CHANNEL_COPY[value].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="flex flex-col items-start gap-1">
            <Button type="submit" loading={save.isPending}>
              Save changes
            </Button>
            {save.isError ? (
              <p className="text-sm text-traffic-red" role="alert">
                {translateSaveError(save.error)}
              </p>
            ) : save.isSuccess ? (
              <p className="text-sm text-muted-foreground">Saved.</p>
            ) : null}
          </div>
        </form>
      )}

      <p className="text-sm text-muted-foreground">
        New to besideU?{' '}
        <Link to="/manager/welcome" className="font-medium text-primary hover:underline">
          View the welcome guide again
        </Link>
        .
      </p>
    </section>
  );
}
